import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BadgeCheck, Inbox, PoundSterling, Smartphone, ShieldCheck, MapPin, FileText, Camera, Star, ChevronDown } from "lucide-react";
import SEO from "@/lib/seo-static.json";
import { SITE } from "@/lib/site";
import { Container, Section, SectionHeader, Reveal, Kicker, CTAButton, usePageTitle } from "@/components/ui-kit";
import { BrowserFrame } from "@/components/mockups/primitives";
import { Mockup } from "@/components/mockups";
import { FAQSection } from "@/components/sections/Social";

export const CONTRACTOR_REGISTER_URL = "https://fixlr.app/marketplace/register";

const BENEFITS = [
  { icon: PoundSterling, title: "Free to list. No commission.", desc: "Your listing costs nothing and Fixlr never takes a cut of the work. Customers pay for the Marketplace; you keep the price you agree." },
  { icon: BadgeCheck, title: "Vetted badge, real reviews", desc: "Insurance and accreditations verified before you go live; reviews come only from completed Fixlr jobs — so the badge means something to the person choosing." },
  { icon: Inbox, title: "Requests with the job pack attached", desc: "Quote requests arrive with scope, photos, access notes, the asset manual and the customer's RAMS template. You price the job, not the mystery." },
  { icon: Smartphone, title: "Win it, run it in Supplier Portal", desc: "Accept and you're set up as a supplier on the job with a portal login — RAMS, photos, sign-off and invoice from your phone. Same login if you later want your own Fixlr workspace." },
];

const STEPS = [
  { title: "Register", desc: "Company details, the trades you cover, your service area (postcode + radius), logo and a few photos. Ten minutes." },
  { title: "Verify", desc: "Upload public liability insurance and any accreditations — Gas Safe, NICEIC, CHAS, SafeContractor. Fixlr checks them and approves your listing." },
  { title: "Get requests", desc: "Customers near you send quote requests and job offers; you reply and message in-thread. Every Monday at 09:00 you get a digest of your inbox plus anonymised demand nearby." },
  { title: "Deliver & grow", desc: "Run the job in Supplier Portal, invoice from it, collect a review. Good reviews and fast replies move you up the results." },
];

const NEEDS = [
  { icon: FileText, label: "Company name, trades and a short description" },
  { icon: MapPin, label: "Service area — base postcode and how far you'll travel" },
  { icon: ShieldCheck, label: "Public liability insurance certificate (in date)" },
  { icon: BadgeCheck, label: "Accreditations, if you have them — they lift your ranking" },
  { icon: Camera, label: "Logo and a handful of photos of your work" },
  { icon: Star, label: "That's it — reviews build themselves from completed jobs" },
];

const FAQS = [
  { q: "Is it really free?", a: "Yes. Listing on the Fixlr Marketplace is free for contractors and there is no commission on work you win. The customers pay a flat monthly add-on for access to the Marketplace." },
  { q: "Who are the customers?", a: "Facilities management companies, property managers, landlords and maintenance contractors who run their operations on Fixlr. They search by trade and radius from the property that needs the work." },
  { q: "How is my listing approved?", a: "Fixlr verifies your insurance and accreditations before the listing goes live and tracks their expiry dates. If you edit an approved listing, the previous version stays live until the change is approved. Listings reported by three customers are hidden automatically while we review." },
  { q: "Do I need to be a Fixlr customer?", a: "No. Registering gives you a free account with your listing and Supplier Portal access for the jobs you win. If you later want your own workspace to run all your work, it's the same login." },
  { q: "How do I get paid?", a: "You agree the price with the customer. Invoices go through Supplier Portal against the job; many customers pay by card link or Direct Debit through Fixlr, others by bank transfer — either way the paper trail is on the job." },
  { q: "Can I turn requests off when I'm busy?", a: "Yes — pause your listing or narrow your radius at any time from your profile. Saved searches on the customer side will pick you up again when you switch back on." },
];

const Hero = () => (
  <section className="relative overflow-hidden" data-testid="contractors-hero">
    <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
    <Container className="relative pt-14 pb-14 lg:pt-20">
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-6 min-w-0">
          <Kicker>For contractors</Kicker>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text leading-[1.02] text-balance">Get found by the teams that raise the jobs.</h1>
          <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed max-w-xl">List your business on the Fixlr Contractor Marketplace — free, no commission. Facilities and property teams search by trade and distance from the property, send you the job pack, and you quote from your phone.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <CTAButton href={CONTRACTOR_REGISTER_URL} testId="contractors-register-btn">List your business — free <ArrowRight className="h-4 w-4" /></CTAButton>
            <a href="#how" onClick={(e) => { e.preventDefault(); document.getElementById("how")?.scrollIntoView({ behavior: "smooth" }); }} className="btn-ghost" data-testid="contractors-how-link">How it works <ChevronDown className="h-4 w-4" /></a>
          </div>
          <dl className="mt-10 grid grid-cols-3 gap-4 max-w-md" data-testid="contractors-stats">
            {[["Free", "listing, forever"], ["0%", "commission on work"], ["Mon 09:00", "demand digest"]].map(([v, l]) => <div key={l}><dt className="font-display text-2xl font-bold text-mk-lime whitespace-nowrap">{v}</dt><dd className="mt-1 text-xs text-mk-muted">{l}</dd></div>)}
          </dl>
        </div>
        <Reveal className="lg:col-span-6 min-w-0" delay={0.1}>
          <BrowserFrame url="fixlr.app/marketplace/me"><Mockup name="listing" /></BrowserFrame>
        </Reveal>
      </div>
    </Container>
  </section>
);

const Benefits = () => (
  <Section className="bg-mk-surface/40 border-y border-mk-border" data-testid="contractors-benefits">
    <Container>
      <SectionHeader eyebrow="Why list" title="Work comes to you with the details already attached." />
      <div className="mt-10 grid sm:grid-cols-2 gap-4">
        {BENEFITS.map((b, i) => (
          <Reveal key={b.title} delay={i * 0.06}>
            <div className="card-dark p-6 h-full" data-testid={`contractors-benefit-${i}`}>
              <span className="grid place-items-center h-10 w-10 rounded-xl border border-mk-border bg-mk-bg text-mk-lime"><b.icon className="h-5 w-5" /></span>
              <h3 className="mt-4 font-display text-lg font-bold text-mk-text">{b.title}</h3>
              <p className="mt-2 text-sm text-mk-dim leading-relaxed">{b.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </Container>
  </Section>
);

const HowItWorks = () => (
  <Section id="how" className="scroll-mt-20" data-testid="contractors-how">
    <Container>
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5 min-w-0">
          <SectionHeader eyebrow="How it works" title="Four steps from sign-up to your first quote request." />
          <ol className="mt-8 space-y-5">
            {STEPS.map((s, i) => (
              <li key={s.title} className="flex gap-4" data-testid={`contractors-step-${i}`}>
                <span className="font-mono text-sm text-mk-lime pt-0.5">0{i + 1}</span>
                <div><div className="font-semibold text-mk-text">{s.title}</div><p className="mt-1 text-sm text-mk-dim leading-relaxed">{s.desc}</p></div>
              </li>
            ))}
          </ol>
        </div>
        <Reveal className="lg:col-span-7 min-w-0" delay={0.1}>
          <div className="eyebrow mb-3">What customers see</div>
          <BrowserFrame url="fixlr.app/marketplace/requests/QR-0931"><Mockup name="quoterequest" /></BrowserFrame>
        </Reveal>
      </div>
    </Container>
  </Section>
);

const Needs = () => (
  <Section className="bg-mk-surface/40 border-y border-mk-border" data-testid="contractors-needs">
    <Container>
      <div className="grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-5 min-w-0">
          <SectionHeader eyebrow="What you'll need" title="Ten minutes and your insurance certificate." desc="Everything else — reviews, response times, completed-job history — builds itself as you work through Fixlr." />
          <div className="mt-8"><CTAButton href={CONTRACTOR_REGISTER_URL} testId="contractors-register-btn-mid">Start your listing <ArrowRight className="h-4 w-4" /></CTAButton></div>
        </div>
        <div className="lg:col-span-7 min-w-0">
          <ul className="grid sm:grid-cols-2 gap-3">
            {NEEDS.map((n, i) => <li key={n.label} className="flex items-start gap-3 rounded-xl border border-mk-border bg-mk-bg p-4 text-sm text-mk-text/90" data-testid={`contractors-need-${i}`}><n.icon className="h-4 w-4 text-mk-lime shrink-0 mt-0.5" />{n.label}</li>)}
          </ul>
        </div>
      </div>
    </Container>
  </Section>
);

const RegisterBand = () => (
  <Section className="pb-24" data-testid="contractors-cta">
    <Container>
      <Reveal className="relative overflow-hidden rounded-3xl border border-mk-border bg-mk-surface p-10 sm:p-16 text-center beam">
        <div className="absolute inset-0 bg-grid-dark opacity-40" aria-hidden />
        <div className="orb h-[400px] w-[400px] -top-40 left-1/2 -translate-x-1/2 bg-mk-lime/20" aria-hidden />
        <div className="relative">
          <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight text-mk-text text-balance">List your business today. Free, forever.</h2>
          <p className="mt-4 text-mk-dim max-w-xl mx-auto">Registration takes about ten minutes. Your listing goes live as soon as your insurance is verified — usually within one working day.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <CTAButton href={CONTRACTOR_REGISTER_URL} testId="contractors-register-btn-bottom">Register on fixlr.app <ArrowRight className="h-4 w-4" /></CTAButton>
            <Link to="/product/marketplace" className="btn-ghost" data-testid="contractors-marketplace-link">See the Marketplace from the customer side</Link>
          </div>
          <p className="mt-6 text-xs text-mk-muted">Questions? <a href={`mailto:${SITE.email}`} className="text-mk-lime hover:underline" data-testid="contractors-email-link">{SITE.email}</a></p>
        </div>
      </Reveal>
    </Container>
  </Section>
);

export default function Contractors() {
  usePageTitle(SEO["/for-contractors"].title, SEO["/for-contractors"].description, SEO["/for-contractors"].image);
  return (
    <div data-testid="contractors-page">
      <Hero />
      <Benefits />
      <HowItWorks />
      <Needs />
      <FAQSection items={FAQS} eyebrow="Contractor FAQ" title="Straight answers before you sign up." />
      <RegisterBand />
    </div>
  );
}
